import { useEffect, useMemo, useRef } from "react";
import { Location } from "../../../../../graphql/operations";
import useAvailability, {
  isGroupAvailable,
} from "../../../../../hooks/useAvailability";
import { formatDate } from "../../../../../utils/calendarUtils";
import styles from "../../Checkout.module.scss";
import DefaultGuide from "../DefaultGuide";
import GuidePicker from "../GuidePicker";

type GuidePickerControllerProps = {
  productId: string;
  location: Location;
  date: Date;
  time: string;
  value: string | undefined;
  onChange: (guideId: string) => void;
  groupSize: number;
  isPrivate: boolean;
} & Pick<ReturnType<typeof useAvailability>, "availabilityMap">;

export default function GuidePickerController({
  productId,
  location,
  date,
  time,
  availabilityMap,
  groupSize,
  isPrivate,
  ...props
}: GuidePickerControllerProps) {
  const { onChange, value } = props;
  const previousValueRef = useRef(value);
  const availableGuides = useMemo(
    () =>
      availabilityMap
        .get(formatDate(date))
        ?.timeSlots.find((timeSlot) => timeSlot.time === time)
        ?.groups.filter(
          (group) => isGroupAvailable(group, groupSize, isPrivate).available
        )
        .flatMap((group) => group.guides) ?? [],
    [availabilityMap, date, groupSize, isPrivate, time]
  );

  const userHasJapaneseTimezone = useMemo(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ["Asia/Tokyo", "Japan"].includes(userTimezone);
  }, []);

  useEffect(() => {
    const isGuideAvailable = availableGuides.find((g) => g.id === value);

    // Only call onChange if the value has changed
    if (!isGuideAvailable && previousValueRef.current !== value) {
      onChange(undefined);
    }

    // Update the ref to the current value
    previousValueRef.current = value;
  }, [availableGuides, value, onChange]);

  if (!userHasJapaneseTimezone) return null;

  return (
    <fieldset className={styles.BookingFormFieldset}>
      <legend>Your Guide</legend>
      <div className={styles.BookingFormFieldsetFields}>
        {availableGuides.length === 0 ? (
          <DefaultGuide locationName={location.translation.name} />
        ) : (
          <GuidePicker {...props} guides={availableGuides} />
        )}
      </div>
    </fieldset>
  );
}
